import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";
import { Pencil, Clock, CalendarDays, Repeat, FileText, Eye, EyeOff, CheckCircle2, Backpack, MessageCircle } from "lucide-react";
import { format, parseISO, formatDistanceToNow } from "date-fns";
import { nb } from "date-fns/locale";
import { cn } from "@/lib/utils";
import type { Reminder } from "@/hooks/useReminders";
import type { LucideIcon } from "lucide-react";

interface EffectiveStatus {
  seen_by_child_at: string | null;
  confirmed_at: string | null;
  isRepeating: boolean;
  scheduledTime: string | null;
  isOverdue: boolean;
  group: "overdue" | "today" | "coming";
}

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  reminder: Reminder | null;
  status: EffectiveStatus | null;
  onEdit: () => void;
}

/** MVP: Only Beskjed / Ta med / Frist. Legacy action/event are shown as Beskjed via effectiveCategory. */
const CATEGORY_META: Record<string, { label: string; emoji: string; Icon: LucideIcon }> = {
  take_along: { label: "Ta med", emoji: "🎒", Icon: Backpack },
  deadline: { label: "Frist", emoji: "⏰", Icon: Clock },
  info: { label: "Beskjed", emoji: "💬", Icon: MessageCircle },
};

function DetailRow({ icon: Icon, label, value, className }: { icon: LucideIcon; label: string; value: string; className?: string }) {
  return (
    <div className="flex items-center gap-3 py-2">
      <Icon className="h-4 w-4 text-muted-foreground shrink-0" />
      <div className="flex-1 min-w-0">
        <p className="text-xs text-muted-foreground">{label}</p>
        <p className={cn("text-sm font-medium", className)}>{value}</p>
      </div>
    </div>
  );
}

export function ReminderDetailSheet({ open, onOpenChange, reminder, status, onEdit }: Props) {
  if (!reminder) return null;

  const effectiveCategory = reminder.category === "action" || reminder.category === "event" ? "info" : (reminder.category || "info");
  const catMeta = CATEGORY_META[effectiveCategory] || { label: "Annet", emoji: "📋", Icon: FileText };

  // Status display
  const statusInfo = (() => {
    if (!status) return { label: "Ukjent", color: "text-muted-foreground", Icon: EyeOff };
    if (status.confirmed_at) return { label: "Bekreftet", color: "text-[hsl(var(--success))]", Icon: CheckCircle2 };
    if (status.seen_by_child_at) return { label: "Sett av barnet", color: "text-primary", Icon: Eye };
    return { label: "Ikke sett", color: "text-muted-foreground", Icon: EyeOff };
  })();

  // Time display
  const timeDisplay = (() => {
    if (status?.scheduledTime) return status.scheduledTime;
    if (reminder.remind_at) {
      try {
        return format(parseISO(reminder.remind_at), "HH:mm");
      } catch { return null; }
    }
    return null;
  })();

  // Date display
  const dateDisplay = (() => {
    if (reminder.remind_date) {
      try {
        return format(parseISO(reminder.remind_date), "EEEE d. MMMM yyyy", { locale: nb });
      } catch { return reminder.remind_date; }
    }
    return null;
  })();

  // Repeat rule display
  const repeatDisplay = (() => {
    if (!reminder.repeat_rule) return null;
    try {
      const parsed = JSON.parse(reminder.repeat_rule);
      if (parsed.days && Array.isArray(parsed.days)) {
        const dayNames = ["", "Man", "Tir", "Ons", "Tor", "Fre", "Lør", "Søn"];
        const days = parsed.days.map((d: { day: number; time?: string }) => {
          const name = dayNames[d.day] || `Dag ${d.day}`;
          return d.time ? `${name} kl ${d.time}` : name;
        });
        return days.join(", ");
      }
    } catch {}
    return "Gjentas";
  })();

  // Source display
  const sourceDisplay = (() => {
    if (reminder.import_batch_id) return "Fra ukeplan";
    return "Manuelt opprettet";
  })();

  // Created at
  const createdDisplay = (() => {
    try {
      return formatDistanceToNow(parseISO(reminder.created_at), { addSuffix: true, locale: nb });
    } catch { return null; }
  })();

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent side="bottom" className="rounded-t-2xl max-h-[85vh] overflow-y-auto">
        <SheetHeader className="pb-2">
          <div className="flex items-center gap-3">
            <span className="text-3xl">{catMeta.emoji}</span>
            <div className="flex-1 min-w-0">
              <SheetTitle className="text-lg font-display text-left">{reminder.title}</SheetTitle>
              <Badge variant="secondary" className="text-[10px] px-2 py-0 mt-1 gap-1">
                <catMeta.Icon className="h-3 w-3" /> {catMeta.label}
              </Badge>
            </div>
          </div>
        </SheetHeader>

        <div className="space-y-1 mt-2">
          {/* Status */}
          <DetailRow
            icon={statusInfo.Icon}
            label="Status"
            value={statusInfo.label}
            className={statusInfo.color}
          />

          {status?.isOverdue && (
            <Badge variant="destructive" className="text-xs mb-2">
              ⚠️ Forfalt
            </Badge>
          )}

          <Separator />

          {/* Date */}
          {dateDisplay && (
            <DetailRow icon={CalendarDays} label="Dato" value={dateDisplay} />
          )}

          {/* Time */}
          {timeDisplay && (
            <DetailRow icon={Clock} label="Klokkeslett" value={`Kl ${timeDisplay}`} />
          )}

          {/* Repeat */}
          {repeatDisplay && (
            <DetailRow icon={Repeat} label="Gjentas" value={repeatDisplay} />
          )}

          {(dateDisplay || timeDisplay || repeatDisplay) && <Separator />}

          {/* Source */}
          <DetailRow icon={FileText} label="Kilde" value={sourceDisplay} />

          {/* Created */}
          {createdDisplay && (
            <DetailRow icon={Clock} label="Opprettet" value={`Opprettet ${createdDisplay}`} />
          )}

          {/* Seen timestamp */}
          {status?.seen_by_child_at && (
            <DetailRow
              icon={Eye}
              label="Sett av barnet"
              value={format(parseISO(status.seen_by_child_at), "d. MMM HH:mm", { locale: nb })}
              className="text-primary"
            />
          )}

          {/* Confirmed timestamp */}
          {status?.confirmed_at && (
            <DetailRow
              icon={CheckCircle2}
              label="Bekreftet"
              value={format(parseISO(status.confirmed_at), "d. MMM HH:mm", { locale: nb })}
              className="text-[hsl(var(--success))]"
            />
          )}

          {/* Original text from import */}
          {reminder.original_text && (
            <>
              <Separator />
              <div className="py-2">
                <p className="text-xs text-muted-foreground mb-1">Originaltekst fra ukeplan</p>
                <p className="text-sm bg-muted/50 rounded-lg p-3 italic">{reminder.original_text}</p>
              </div>
            </>
          )}
        </div>

        {/* Edit button */}
        <div className="pt-4 pb-2">
          <Button
            variant="outline"
            className="w-full rounded-xl gap-2"
            onClick={onEdit}
          >
            <Pencil className="h-4 w-4" />
            Rediger påminnelse
          </Button>
        </div>
      </SheetContent>
    </Sheet>
  );
}
